<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { taskCategoriesApi, type TaskCategory } from '@/api/task-categories';

interface Props {
  taskCategory: TaskCategory;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const discardMutation = useMutation({
  mutationFn: () => taskCategoriesApi.destroy(props.taskCategory.id),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  discardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Archivar Categoría "{{ taskCategory.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea archivar la categoría "{{ taskCategory.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La categoría será archivada junto con todas sus definiciones de tareas.
        Esta acción se puede revertir más tarde si es necesario.
      </p>
      <v-alert
        v-if="taskCategory.taskDefinitionsCount > 0"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        Esta categoría tiene {{ taskCategory.taskDefinitionsCount }} definiciones de tareas
        que también serán archivadas.
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="discardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="warning"
        :loading="discardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Archivar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="discardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
